"use strict";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Box, Button, Grid, Group, Input, MultiSelect, MultiSelectProps, rem, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedCallback, useDisclosure } from "@mantine/hooks";
import { IconBarcode, IconBuildingSkyscraper, IconBuildingWarehouse, IconFileExport, IconSearch } from "@tabler/icons-react";
import {
    ColDef,
    GridReadyEvent,
    ModuleRegistry
} from "ag-grid-community";
import {
    IServerSideDatasource,
    IServerSideGetRowsRequest,
    ServerSideRowModelModule
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { paginationPageSize, paginationPageSizeSelector, repositoryDelivery, repositoryPos } from "../../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { formatDateTransfer } from "../../../../common/FormatDate/FormatDate";
import { formatPrice } from "../../../../common/formatPrice/PriceFunction";
import LoadingOverlayCustom from "../../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_DELIVERY } from "../../../../config";
import { InventoryBySerialModel } from "../../../../model/InventoryBySerialModel";
import { MessageResponse } from "../../../../model/MessageResponse";


ModuleRegistry.registerModules([
    ServerSideRowModelModule
]);

const InventoryBySerial = () => {
    const [loading, setLoading] = useState(true);
    const gridRef = useRef<AgGridReact<any>>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const datasourceRef = useRef<IServerSideDatasource | null>(null);
    const [height, setHeight] = useState(800);
    const [dataBranchCode, setDataBranchCode] = useState<any[]>([]);
    const [dataInvCode, setDataInvCode] = useState<any[]>([]);
    const [dataItemCode, setDataItemCode] = useState<any[]>([]);
    const [dataCatSegment4d, setDataCatSegment4d] = useState<any[]>([]);
    const [searchCatSegment4d, setSearchCatSegment4d] = useState("");
    const [visible, { toggle, close, open }] = useDisclosure(false);
    // Xoá useState search, chỉ dùng form
    const containerStyle = useMemo(() => ({ width: "100%", height: height }), [height]);
    const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
    const sizeGrid = { base: 12, sm: 6, md: 6, lg: 3 };

    useEffect(() => {
        const updateHeight = () => {
            const headerHeight = headerRef.current?.offsetHeight || 0;
            // 400 là tổng khoảng cách cần trừ (breadcrumb, filters, padding, pagination)
            const newHeight = window.innerHeight - (210 + headerHeight);
            setHeight(newHeight);
        };

        // Đợi DOM render xong rồi mới tính
        setTimeout(updateHeight, 0);
        window.addEventListener("resize", updateHeight);

        return () => {
            window.removeEventListener("resize", updateHeight);
        };
    }, []);

    // Chỗ này để làm lọc dùng form cho đỡ lag so với state
    const form = useForm<any>({
        initialValues: {
            keySearch: "",
            serial: "",
            branchCode: [],
            invCode: [],
            itemCode: [],
            maChungs: [],
        },
    });
    const fetchData = async (request: IServerSideGetRowsRequest) => {
        try {
            const currentPageSize = gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
            const skip = request.startRow || 0;
            const take = currentPageSize;
            let url = `/api/v1/TblInventorySerialOnhand/get-list-serial-v3?Skip=${skip}&Take=${take}`;
            const values = form.getValues();
            if (values.branchCode && values.branchCode.length > 0) {
                values.branchCode.forEach((branchCode: string) => {
                    url += `&BranchCode=${branchCode}`;
                });
            }
            if (values.invCode && values.invCode.length > 0) {
                values.invCode.forEach((invCode: string) => {
                    url += `&InvCode=${invCode}`;
                });
            }
            if (values.itemCode && values.itemCode.length > 0) {
                values.itemCode.forEach((itemCode: string) => {
                    url += `&ItemCode=${itemCode}`;
                });
            }
            if (values.maChungs && values.maChungs.length > 0) {
                url += values.maChungs?.map((item: string) => `&MaChungs=${item}`).join("");
            }
            if (values.keySearch) {
                url += `&KeySearch=${values.keySearch.trim().toLowerCase()}`;
            }
            if (values.serial) {
                url += `&SerialList=${values.serial.trim().toLowerCase()}`;
            }
            return await repositoryDelivery.get<MessageResponse<InventoryBySerialModel[]>>(url);
        } catch (error) {
            console.error(error);
        }
    };
    // Call api bên server
    const getServerSideDatasource = useCallback((): IServerSideDatasource => {
        return {
            getRows: async (params) => {
                console.log("getRows called with filters:", form.getValues());
                var response = await fetchData(params.request);
                console.log("Response data:", response);
                console.log("First item:", response?.data?.[0]);
                // adding delay to simulate real server call
                if (response && response.success) {
                    // Tách row TOTAL ra khỏi data chính
                    const allData = response.data || [];

                    params.success({
                        rowData: allData,
                        rowCount: (response as any).totalCount,
                    });
                    setLoading(false);
                } else {
                    // inform the grid request failed
                    params.fail();
                    setLoading(false);
                }
            },
        };
    }, []);
    // Sau khi chọn tìm kiếm xong thì sẽ thực hiện click để call api này 
    const handleSearch = useCallback(() => {
        console.log("Search clicked with values:", form.getValues());
        setLoading(true);
        const api = gridRef.current?.api;
        if (!api) {
            console.log("API not ready");
            return;
        }

        // Refresh server-side cache để gọi lại getRows với filter mới
        (api as any).refreshServerSide({ purge: true });
    }, []);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        // đăng datasource đúng để ag-Grid gọi getRows
        const datasource = getServerSideDatasource();
        datasourceRef.current = datasource;
        params.api.setGridOption('serverSideDatasource', datasource);
        console.log("Grid ready, datasource registered");
    }, [getServerSideDatasource]);

    const getRowId = useCallback((params: any) => {
        return params.data?.mA_NHAN_VIEN || Math.random().toString();
    }, []);

    const getRowStyle = useCallback((params: any) => {
        if (params.node.rowPinned) {
            return {
                backgroundColor: '#e7f5ff',
                fontWeight: 700,
                fontSize: '14px',
                borderTop: '3px solid #15aabf',
            };
        }
        return undefined;
    }, []);

    // Hàm sửa lý sự kiện sắp xếp
    const handleSortChanged = useCallback(() => {
        const columnState = gridRef.current?.api?.getColumnState() || [];
        // tìm cột có sort (asc/desc)
        const sortedCol = columnState.find((c: any) => c.sort);
        if (sortedCol) {
            form.setFieldValue("SortBy", sortedCol.colId);
            form.setFieldValue("SortDirection", (sortedCol.sort || "").toUpperCase() === "ASC" ? "ASC" : "DESC");
        } else {
            form.setFieldValue("SortBy", null);
            form.setFieldValue("SortDirection", null);
        }
        // reload server-side data với sort mới
        gridRef.current?.api?.refreshServerSide?.({ purge: true });
    }, [form]);

    const [columnDefs] = useState<ColDef[]>([
        {
            field: "soPhieuNhap", headerName: "Số phiếu nhập", width: 150
        },
        { field: "maBarcode", headerName: "Mã vạch", width: 130 },
        {
            field: "branchCode", headerName: "Mã chi nhánh", width: 150
        },
        { field: "branchName", headerName: "Tên chi nhánh", width: 180 },
        { field: "maBp", headerName: "Mã bộ phận", width: 150 },
        { field: "maNv", headerName: "Mã nhân viên", width: 200 },
        { field: "nguoiDn", headerName: "Người đề nghị", width: 150 },
        { field: "maNcc", headerName: "Mã NCC", width: 120 },
        { field: "tenNcc", headerName: "Tên NCC", width: 180 },
        { field: "maKho", headerName: "Mã kho", width: 130 },
        { field: "tenKho", headerName: "Tên kho", width: 180 },
        { field: "maVt", headerName: "Mã vị trí", width: 130 },
        { field: "tenVt", headerName: "Tên vị trí", width: 180 },
        { field: "maSp", headerName: "Mã sản phẩm", width: 150, pinned: "left", },
        { field: "maNganh", headerName: "Mã ngành", width: 120 },
        { field: "tenNganh", headerName: "Tên ngành", width: 180 },
        { field: "maLoai", headerName: "Mã loại", width: 120 },
        { field: "tenLoai", headerName: "Tên loại", width: 150 },
        { field: "maChung", headerName: "Mã chủng", width: 120 },
        { field: "tenChung", headerName: "Tên chủng loại", width: 180 },
        { field: "maNhom", headerName: "Mã nhóm", width: 120 },
        { field: "tenNhom", headerName: "Tên nhóm", width: 150 },
        { field: "tenSp", headerName: "Tên sản phẩm", width: 300 },
        { field: "dvt", headerName: "Đơn vị tính", width: 120 },
        { field: "soLuong", headerName: "Số lượng", width: 110, type: "numericColumn", valueFormatter: (p) => p.value ?? "" },
        { field: "ngayNhap", headerName: "Ngày nhập", width: 140, valueFormatter: (p) => p.value ? formatDateTransfer(p.value) : "" },
        { field: "tuoiTon", headerName: "Tuổi tồn", width: 120, valueFormatter: (p) => p.value ?? "" },
        { field: "donGiaNhap", headerName: "Đơn giá nhập", width: 140, valueFormatter: (p) => formatPrice(p.value) },
        { field: "segment1Code", headerName: "Mã phân đoạn 1", width: 120 },
        { field: "segment1Name", headerName: "Tên phân đoạn 1", width: 150 },
        { field: "segment6Code", headerName: "Mã phân đoạn 6", width: 120 },
        { field: "segment6Name", headerName: "Tên phân đoạn 6", width: 150 },
        { field: "plvt1Code", headerName: "Mã plvt 1", width: 120 },
        { field: "plvt1Name", headerName: "Tên plvt 1", width: 150 },
        { field: "plvt3Code", headerName: "Mã plvt 3", width: 120 },
        { field: "plvt3Name", headerName: "Tên plvt 3", width: 150 },
        { field: "plvt4Code", headerName: "Mã plvt 4", width: 120 },
        { field: "plvt4Name", headerName: "Tên plvt 4", width: 150 },
        { field: "plvt5Code", headerName: "Mã plvt 5", width: 120 },
        { field: "plvt5Name", headerName: "Tên plvt 5", width: 150 },
        { field: "plvt6Code", headerName: "Mã plvt 6", width: 120 },
        { field: "plvt6Name", headerName: "Tên plvt 6", width: 150 },
    ]);

    const defaultColDef = useMemo<ColDef>(() => {
        return {
            sortable: true,
            resizable: true,
        };
    }, []);

    // Lấy dữ liệu mã kho theo chi nhánh đã chọn
    const DataInvCode = async () => {
        const branchSearch = dataBranchCode.length > 0 && form.getValues()?.branchCode?.length > 0
            ? form.getValues()?.branchCode
                .map((branchCode: string) => {
                    const branch = dataBranchCode.find((b) => b.value === branchCode);
                    return branch ? `&BranchId=${branch.id}` : "";
                })
                .join("")
            : "";

        const take = form.getValues()?.branchCode?.length * 100;

        const res = await repositoryDelivery.get<MessageResponse<any>>(
            `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=${form.getValues()?.branchCode && form.getValues()?.branchCode?.length > 0 ? take : 100
            }${branchSearch}`
        );

        if (res && res.success) {
            const result = res.data;
            const data = result
                ?.filter((item: any) => item.idBranch && item.name)
                ?.map((item: any) => ({
                    label: `${item.code} - ${item.name}`,
                    value: item.code?.toString(),
                }));
            setDataInvCode(data);
        }
    };

    // Lấy dữ liệu mã chi nhánh
    const DataBrachCode = async () => {
        try {
            const response = await repositoryPos.get(
                `api/v1/TblDmInventory/get-select-branch?auth=true`
            );
            if (response && response.success) {
                const transformedData = response.data
                    .filter((i: any) => i.att2 && i.text)
                    .map((item: any) => {
                        return {
                            value: item.att2,
                            label: item.att2 + " - " + item.text,
                            id: item.value,
                        };
                    });

                setDataBranchCode(transformedData);
            }
        } catch (error) {
            console.error(error);
            return;
        }
    };
    // Lấy dữ liệu mã sản phẩm
    const DataItemCode = async (keySearch: string) => {
        try {
            let url = "";
            if (keySearch && keySearch.length > 0) {
                url += `&ItemCodes=${keySearch}`;
            }
            const response = await repositoryPos.get(
                `/api/v1/TblItem/get-select?Key=${keySearch}&Page=30`
            );
            if (response && response.success) {
                const transformedData = response.data
                    ?.filter((i: any) => i.value && i.text)
                    .map((branch: any) => ({
                        value: branch.value,
                        label: branch.text,
                    }));
                const uniqueData = Array.from(
                    new Set(transformedData.map((item: any) => item.value))
                ).map((value) =>
                    transformedData.find((item: any) => item.value === value)
                );
                setDataItemCode(uniqueData);
            }
        } catch (error) {
            console.error(error);
            return;
        }
    };

    // Sử dụng debounce để trì hoãn việc gọi API
    const debouncedDataItemCode = useDebouncedCallback((value: string) => {
        if (value.length >= 3) {
            DataItemCode(value); // Gọi API với từ khóa tìm kiếm
        }
    }, 500);

    const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
        option,
    }) => (
        <Group gap="sm">
            <Text>{dataItemCode.find((x) => x.value === option.value)?.label}</Text>
        </Group>
    );

    // Lấy dữ liệu mã chủng
    const fetchCatSegment4d = async () => {
        let url = "/api/v1/TblCatSegment4d/get-list?Skip=0&Take=20";

        if (searchCatSegment4d && searchCatSegment4d.length > 0) {
            url += `&CodeString=${searchCatSegment4d}`;
        }

        const res = await repositoryPos.get<MessageResponse<any>>(url);

        if (res && res.success) {
            const dataAPI = res.data
                ?.filter((item: any) => item.codeString !== null)
                ?.map((item: any) => ({
                    label: `${item.codeString} - ${item.name}`,
                    value: item.codeString?.toString(),
                }));
            setDataCatSegment4d(dataAPI);
        }
    };

    const exportDataExcel = async () => {
        open(); // Hiển thị loading
        try {
            // // Lấy page hiện tại / pageSize giống fetchData
            const currentPageSize =
                gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
            const currentPage =
                gridRef.current?.api?.paginationGetCurrentPage?.() || 0;
            const skip = currentPage * currentPageSize;
            const take = currentPageSize;



            let url = `/api/v1/TblInventorySerialOnhand/export-list-serial-v3?Skip=${skip}&Take=${take}`;


            const values = form.getValues();
            if (values.branchCode && values.branchCode.length > 0) {
                values.branchCode.forEach((branchCode: string) => {
                    url += `&BranchCode=${branchCode}`;
                });
            }
            if (values.invCode && values.invCode.length > 0) {
                values.invCode.forEach((invCode: string) => {
                    url += `&InvCode=${invCode}`;
                });
            }
            if (values.itemCode && values.itemCode.length > 0) {
                values.itemCode.forEach((itemCode: string) => {
                    url += `&ItemCode=${itemCode}`;
                });
            }
            if (values.maChungs && values.maChungs.length > 0) {
                url += values.maChungs?.map((item: string) => `&MaChungs=${item}`).join("");
            }
            if (values.keySearch) {
                url += `&KeySearch=${values.keySearch.trim().toLowerCase()}`;
            }
            if (values.serial) {
                url += `&SerialList=${values.serial.trim().toLowerCase()}`;
            }
            // Object.entries(keySearch).forEach(([key, value]) => {
            //     if (value) {
            //         url += `&${key}=${encodeURIComponent(value)}`;
            //     }
            // });
            const _url = BASE_API_DELIVERY + url;
            const fullUrl = new URL(_url);
            const token = localStorage.getItem("token");

            // Gọi API
            const response = await fetch(fullUrl.toString(), {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token?.replace(/"/g, "")}`,
                    "Content-Type": "application/vnd.ms-excel",
                },
            });
            // Kiểm tra trạng thái HTTP
            if (!response.ok) {
                const errorResponse = await response.json(); // Parse lỗi từ API
                const errorMessage =
                    errorResponse?.message || "Đã xảy ra lỗi khi xuất dữ liệu!";
                throw new Error(errorMessage); // Ném lỗi để xử lý trong `catch`
            }

            // Xử lý file Excel
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const filename = "bao_cao_ton_kho_theo_ma_vach_theo_nguoi_nhap.xlsx"; // Tên mặc định nếu không có header
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            console.error("Error:", error);
            // Hiển thị thông báo lỗi
            NotificationExtension.Fails(
                error.message || "Đã xảy ra lỗi không xác định!"
            );
        } finally {
            close(); // Đóng loading
        }
    };



    useEffect(() => {
        Promise.all([DataBrachCode(), fetchCatSegment4d()]);
    }, []);

    useEffect(() => {
        DataInvCode();
    }, [form.getValues().branchCode]);

    return (
        <Box pos={"relative"} style={containerStyle}>
            <LoadingOverlayCustom visible={visible} />
            <div
                style={{
                    border: "1px solid #dee2e6",
                    padding: "10px",
                    overflowX: "hidden",
                }}
            >
                <Group justify="space-between" gap={0}>
                    <BreadcrumbLayout1 />
                    <Button
                        leftSection={<IconFileExport style={{ width: rem(14), height: rem(14) }} />}
                        onClick={exportDataExcel}
                    >
                        Export Excel
                    </Button>
                </Group>
                <Grid mt={10}>
                    <Grid.Col span={sizeGrid}>
                        <Input
                            placeholder="Nhập tìm kiếm"
                            type="text"
                            leftSection={<IconSearch size={20} color="#15aabf" />}
                            value={form.getValues().keySearch}
                            onChange={e => form.setFieldValue("keySearch", e.target.value)}
                            onKeyDown={async (e) => { if (e.key === "Enter") handleSearch(); }}
                        />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <MultiSelect
                            size="sm"
                            placeholder="Nhập mã sản phẩm"
                            clearable
                            searchable
                            data={dataItemCode}
                            renderOption={renderMultiSelectOption}
                            value={form.getValues().itemCode}
                            onSearchChange={(value) => {
                                debouncedDataItemCode(value); // Sử dụng debounce
                            }}
                            onChange={v => form.setFieldValue("itemCode", v)}
                            leftSection={<IconBarcode size={20} color="#15aabf" />}
                        />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <Input
                            placeholder="Nhập mã vạch"
                            type="text"
                            leftSection={<IconSearch size={20} color="#15aabf" />}
                            value={form.getValues().serial}
                            onChange={e => form.setFieldValue("serial", e.target.value)}
                        />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <MultiSelect
                            placeholder="Chi nhánh"
                            clearable
                            searchable
                            data={dataBranchCode}
                            value={form.getValues().branchCode}
                            onChange={v => form.setFieldValue("branchCode", v)}
                            leftSection={<IconBuildingSkyscraper size={20} color="#15aabf" />}
                        />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <MultiSelect
                            size="sm"
                            placeholder="Mã kho"
                            searchable
                            clearable
                            data={dataInvCode}
                            value={form.getValues().invCode}
                            onChange={v => form.setFieldValue("invCode", v)}
                            leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
                        />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <MultiSelect
                            size="sm"
                            placeholder="Nhập mã chủng"
                            clearable
                            searchable
                            data={dataCatSegment4d}
                            value={form.getValues().maChungs}
                            searchValue={searchCatSegment4d}
                            onSearchChange={setSearchCatSegment4d}
                            onChange={v => form.setFieldValue("maChungs", v)}
                            leftSection={<IconBarcode size={20} color="#15aabf" />}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 0.9 }}>
                        <Button
                            color="blue"
                            variant="outline"
                            onClick={handleSearch}
                        >
                            Tìm kiếm
                        </Button>
                    </Grid.Col>
                </Grid>
            </div>
            <div style={gridStyle} className={"ag-theme-quartz"}>
                <AgGridReact<any>
                    ref={gridRef}
                    loading={loading}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowModelType={"serverSide"}
                    pagination={true}
                    paginationPageSize={100}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    onSortChanged={handleSortChanged}
                    onGridReady={onGridReady}
                    getRowId={getRowId}
                    getRowStyle={getRowStyle}
                    localeText={AG_GRID_LOCALE_VN}
                />
            </div>
        </Box>
    );
};
export default InventoryBySerial;
